/// <amd-module name="Wit/WorkItemForm/History/Diffs/TestStepDiff" />
import * as React from "react";
import { filterXSS } from "xss";

import { TestStepChangeType, TestActionTypes } from "Test/TestPlansHubRefresh/TestCaseWorkItemControls/TestSteps/TestStepsUtils/TestStepOM";
import {
    ITestStepChange,
    ITestStepMetaData,
    TestStepsParseUtils
} from "Test/TestPlansHubRefresh/TestCaseWorkItemControls/TestSteps/TestStepsUtils/TestStepsParseUtils";
import { ObservableValue } from "VSS/Core/Observable";
import { format } from "VSS/Core/Util/String";
import { Table, ITableColumn, ITableRowDetails, TableRow, renderColumns, SimpleTableCell } from "VSSUI/Table";
import { ArrayItemProvider } from "VSSUI/Utilities/Provider";
import { WorkItemFieldUpdate } from "Wit/Clients/Wit/Contracts/WorkItemTracking";
import { FieldName } from "Wit/WorkItemForm/Common/Components";
import * as Resources from "Wit/WorkItemForm/Resources";

import { ChangeType } from "../ChangeType";
import { AttachmentDiff, IAttachmentUpdateProps } from "./AttachmentDiff";
import { ILinkUpdateProps, LinkDiff } from "./LinkDiff";

interface ITableItemStep {
    step: string;
    action: JSX.Element | undefined;
    expected: JSX.Element | undefined;
    attachment: JSX.Element | undefined;
    type: ChangeType;
}

export interface ITestStepDiffProps {
    refName: string;
    fieldUpdate: WorkItemFieldUpdate;
    attachmentUpdates?: IAttachmentUpdateProps[];
    linkUpdates?: ILinkUpdateProps[];
}

export function TestStepDiff(props: ITestStepDiffProps) {
    const items = getItems(props);
    const itemsProvider = new ArrayItemProvider(items);

    const renderedItems =
        itemsProvider.length > 0 ? (
            <div className="padding-vertical-4 margin-bottom-8">
                <Table
                    renderRow={rowToRender}
                    itemProvider={itemsProvider}
                    columns={StepTableColumns}
                    className="test-step-diff-table"
                    containerClassName="h-scroll-auto"
                />
            </div>
        ) : null;

    return (
        <div className="html-field-diff">
            <div className="font-size-ml font-weight-semibold margin-bottom-8">
                <FieldName refName={props.refName} />
            </div>
            {renderedItems}
        </div>
    );
}

const getItems = (props: ITestStepDiffProps) => {
    const itemsToRender: (ITableItemStep | "divider")[] = [];
    const testStepChanges: ITestStepChange[] = TestStepsParseUtils.getTestStepChanges(props.fieldUpdate);

    testStepChanges.forEach((stepChange, index) => {
        const newItems: ITableItemStep[] = [];
        const oldItems: ITableItemStep[] = [];
        let stepValue: string = "";
        if (stepChange.newValue && (stepChange.type === TestStepChangeType.Added || stepChange.type === TestStepChangeType.Edited)) {
            if (stepChange.type === TestStepChangeType.Added) {
                stepValue = format("{0} {1}", Resources.AddedStep, stepChange.newValue.stepNumber);
            } else {
                stepValue = format("{0} {1}", Resources.UpdatedStep, stepChange.newValue.stepNumber);
            }

            if (stepChange.newValue.type === TestActionTypes.SharedSteps) {
                newItems.push({
                    step: stepValue,
                    action: getSharedStepLink(props.linkUpdates, stepChange.newValue.action, ChangeType.Added),
                    expected: undefined,
                    attachment: undefined,
                    type: ChangeType.Added
                });
            } else {
                const action = filterXSS(stepChange.newValue.action);
                const expected = filterXSS(stepChange.newValue.expectedResult);

                newItems.push({
                    step: stepValue,
                    action: <ins dangerouslySetInnerHTML={{ __html: action }} />,
                    expected: <ins dangerouslySetInnerHTML={{ __html: expected }} />,
                    attachment: getAttachment(stepChange.newValue, props.attachmentUpdates, ChangeType.Added),
                    type: ChangeType.Added
                });
            }
        }

        if (stepChange.oldValue && (stepChange.type === TestStepChangeType.Deleted || stepChange.type === TestStepChangeType.Edited)) {
            if (stepChange.type === TestStepChangeType.Deleted) {
                stepValue = format("{0} {1}", Resources.DeletedStep, stepChange.oldValue.stepNumber);
            } else {
                stepValue = format("{0} {1}", Resources.PreviousStep, stepChange.oldValue.stepNumber);
            }

            if (stepChange.oldValue.type === TestActionTypes.SharedSteps) {
                newItems.push({
                    step: stepValue,
                    action: getSharedStepLink(props.linkUpdates, stepChange.oldValue.action, ChangeType.Removed),
                    expected: undefined,
                    attachment: undefined,
                    type: ChangeType.Removed
                });
            } else {
                const action = filterXSS(stepChange.oldValue.action);
                const expected = filterXSS(stepChange.oldValue.expectedResult);

                oldItems.push({
                    step: stepValue,
                    action: <del dangerouslySetInnerHTML={{ __html: action }} />,
                    expected: <del dangerouslySetInnerHTML={{ __html: expected }} />,
                    attachment: getAttachment(stepChange.oldValue, props.attachmentUpdates, ChangeType.Removed),
                    type: ChangeType.Removed
                });
            }
        }

        itemsToRender.push(...newItems, ...oldItems);
        if (testStepChanges.length - 1 !== index) {
            itemsToRender.push("divider");
        }
    });

    return itemsToRender;
};

const getAttachment = (step: ITestStepMetaData, attachmentUpdates: IAttachmentUpdateProps[] | undefined, type: ChangeType): JSX.Element => {
    const elements: JSX.Element[] = [];
    attachmentUpdates?.forEach(attachmentUpdate => {
        const attachmentInfo = attachmentUpdate.attachment.attributes;

        if (attachmentInfo.comment === `[TestStep=${step.id}]:` && attachmentUpdate.changeType === type) {
            elements.push(<AttachmentDiff key={step.id} attachment={attachmentUpdate.attachment} changeType={type} />);
        }
    });

    return <>{elements}</>;
};

const getSharedStepLink = (linkUpdates: ILinkUpdateProps[] | undefined, id: number, type: ChangeType) => {
    const elements: JSX.Element[] = [];
    linkUpdates?.forEach(linkUpdate => {
        if (linkUpdate.link.url.endsWith(`/_apis/wit/workItems/${id}`) && linkUpdate.changeType === type) {
            elements.push(<LinkDiff key={id} link={linkUpdate.link} changeType={type} />);
        }
    });

    return <>{elements}</>;
};

const rowToRender = (rowIndex: number, item: ITableItemStep | "divider", details: ITableRowDetails<ITableItemStep>): JSX.Element => {
    if (item === "divider") {
        return <div className="margin-bottom-8"></div>;
    }

    return (
        <TableRow className={item.type} details={details} index={rowIndex} linkProps={(item as any)?.linkProps}>
            {renderColumns(rowIndex, StepTableColumns, item, details)}
        </TableRow>
    );
};

const renderStepCell = (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<ITableItemStep>, tableItem: ITableItemStep): JSX.Element => {
    let element: JSX.Element | string | undefined;
    if (["action", "expected", "attachment", "step"].includes(tableColumn.id)) {
        element = tableItem[tableColumn.id as keyof ITableItemStep];
    }

    return (
        <SimpleTableCell key={"row" + rowIndex + "col" + columnIndex} columnIndex={columnIndex}>
            <div className="history-test-step">{element || ""}</div>
        </SimpleTableCell>
    );
};

const StepTableColumns: ITableColumn<ITableItemStep>[] = [
    {
        id: "step",
        readonly: true,
        renderCell: renderStepCell,
        width: new ObservableValue(-20)
    },
    {
        id: "action",
        name: "Action",
        readonly: true,
        renderCell: renderStepCell,
        width: new ObservableValue(-40)
    },
    {
        id: "expected",
        name: "Expected Result",
        readonly: true,
        renderCell: renderStepCell,
        width: new ObservableValue(-30)
    },
    {
        id: "attachment",
        name: "Attachments",
        readonly: true,
        renderCell: renderStepCell,
        width: new ObservableValue(-30)
    }
];
